import React, { useState, useEffect } from 'react';
import { Plus, Minus } from 'lucide-react';
import pointVoteService from '../services/PointVoteService';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';

/**
 * Point-based voting panel for a sheet in a setlist
 * Displays format: + [my_points] / [total_points] -
 */
const PointVotingPanel = ({ 
  setlistId, 
  sheetId, 
  sheetTitle = 'Unknown Song',
  maxPoints = 0,
  size = 'small',
  className = '',
  canVote = true,
  onVoteChange = null
}) => {
  const { isAuthenticated } = useAuth();
  const { showNotification } = useNotifications();
  const [myPoints, setMyPoints] = useState(0);
  const [totalPoints, setTotalPoints] = useState(0);
  const [voterCount, setVoterCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [remainingPoints, setRemainingPoints] = useState(maxPoints);

  // Load initial point data
  useEffect(() => {
    if (!setlistId || !sheetId) return;
    loadPointData();
  }, [setlistId, sheetId, maxPoints]);

  const loadPointData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load sheet point summary
      const summary = await pointVoteService.getSheetPointSummary(setlistId, sheetId);
      const formattedSummary = pointVoteService.formatPointSummary(summary);
      
      setTotalPoints(formattedSummary.totalPoints);
      setVoterCount(formattedSummary.voterCount);

      // Load user's current allocations if authenticated
      if (isAuthenticated && canVote) {
        try {
          const userAllocations = await pointVoteService.getMyPointAllocations(setlistId);
          const myPointsForSheet = userAllocations.allocations[sheetId] || 0;
          setMyPoints(myPointsForSheet);
          setRemainingPoints(userAllocations.remainingPoints);
        } catch (voteError) {
          console.warn('Could not load user allocations:', voteError.message);
          setMyPoints(0);
          setRemainingPoints(maxPoints);
        }
      }

    } catch (err) {
      console.error('Error loading point data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updatePoints = async (newPointValue) => {
    if (!canVote || !isAuthenticated) return;
    
    try {
      setError(null);
      
      // Get current user allocations
      const currentAllocations = await pointVoteService.getMyPointAllocations(setlistId);
      
      // Update the allocation for this sheet
      const updatedAllocations = {
        ...currentAllocations.allocations,
        [sheetId]: newPointValue
      };

      // Remove zero allocations
      if (newPointValue === 0) {
        delete updatedAllocations[sheetId];
      }

      // Validate the new allocation
      const validation = pointVoteService.validatePointAllocations(updatedAllocations, maxPoints);
      if (!validation.isValid) {
        setError(validation.errors[0]);
        setTimeout(() => setError(null), 3000);
        return;
      }

      // Update allocations on server
      const result = await pointVoteService.updatePointAllocations(setlistId, updatedAllocations);
      
      // Update local state
      setMyPoints(newPointValue);
      setRemainingPoints(result.maxPoints - validation.totalPoints);

      // Update the sheet's total points from the summary
      const sheetSummary = result.summaries[sheetId] || { total_points: 0, voter_count: 0 };
      setTotalPoints(sheetSummary.total_points);
      setVoterCount(sheetSummary.voter_count);

      // Notify parent component of vote change
      if (onVoteChange) {
        onVoteChange(sheetId, sheetSummary);
      }

    } catch (err) {
      console.error('Error updating points:', err);
      setError(err.message);
      setTimeout(() => setError(null), 3000);
    }
  };

  const increasePoints = (e) => {
    e?.stopPropagation(); // Prevent event bubbling to parent clickable elements
    const newValue = myPoints + 1;
    if (remainingPoints >= 1) {
      updatePoints(newValue);
    }
  };

  const decreasePoints = (e) => {
    e?.stopPropagation(); // Prevent event bubbling to parent clickable elements
    const newValue = Math.max(0, myPoints - 1);
    updatePoints(newValue);
  };

  // Don't render if no permissions and no existing votes
  if (!canVote && totalPoints === 0 && !isAuthenticated) {
    return null;
  }

  // Size configuration matching CompactMediaPlayer
  const sizeClasses = {
    small: {
      icon: 'w-3 h-3',
      button: 'p-1',
      gap: 'gap-0.5',
      text: 'text-xs'
    },
    medium: {
      icon: 'w-4 h-4', 
      button: 'p-1.5',
      gap: 'gap-1',
      text: 'text-sm'
    }
  };

  const { icon: iconSize, button: buttonPadding, gap, text: textSize } = sizeClasses[size] || sizeClasses.small;

  const getButtonStyles = (disabled = false) => {
    const baseStyles = `${buttonPadding} rounded transition-all duration-200`;
    
    if (disabled) {
      return `${baseStyles} text-gray-300 cursor-not-allowed`;
    }
    
    return `${baseStyles} text-gray-600 hover:text-gray-900 hover:bg-gray-100`;
  };

  if (loading) {
    return (
      <div className={`flex items-center ${gap} ${className}`}>
        <div className={`${textSize} text-gray-400`}>Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center ${gap} ${className}`}>
        <div className={`${textSize} text-red-400`}>Error</div>
      </div>
    );
  }

  return (
    <div 
      className={`flex items-center ${gap} ${className}`}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Decrease Button */}
      <button
        onClick={decreasePoints}
        disabled={!canVote || !isAuthenticated || myPoints <= 0}
        className={getButtonStyles(!canVote || !isAuthenticated || myPoints <= 0)}
        title="Remove 1 point"
      >
        <Minus className={iconSize} />
      </button>

      {/* Points Display */}
      <div className={`${textSize} font-medium text-gray-700 min-w-[40px] text-center`}>
        {isAuthenticated && canVote ? `${myPoints}/${totalPoints}` : totalPoints}
      </div>

      {/* Increase Button */}
      <button
        onClick={increasePoints}
        disabled={!canVote || !isAuthenticated || remainingPoints <= 0}
        className={getButtonStyles(!canVote || !isAuthenticated || remainingPoints <= 0)}
        title="Add 1 point"
      >
        <Plus className={iconSize} />
      </button>
    </div>
  );
};

export default PointVotingPanel;